import React, { useEffect, useRef } from 'react';

const PaciMap = ({ lands = [], language = 'ar', onOpenDetail }) => {
  const iframeRef = useRef(null);

  useEffect(() => {
    // Filter lands with paci_coordinates and published
    const landsWithPaci = lands.filter(land => land.paci_coordinates && land.published);
    
    console.log('PaciMap - Total lands received:', lands.length);
    console.log('PaciMap - Lands with PACI coordinates:', landsWithPaci.length);
    console.log('PaciMap - Filtered lands:', lands);

    // Function to send data to iframe
    const sendDataToIframe = () => {
      if (iframeRef.current && iframeRef.current.contentWindow) {
        iframeRef.current.contentWindow.postMessage({
          type: 'UPDATE_MARKERS',
          lands: landsWithPaci,
          language: language
        }, window.location.origin);
      }
    };

    // Listen for map ready message from iframe
    const handleMessage = (event) => {
      // Filter out React DevTools and other non-app messages
      if (!event.data || !event.data.type) return;
      if (event.data.source && event.data.source.includes('react-devtools')) return;
      
      // Allow messages from same origin or localhost
      const allowedOrigins = [window.location.origin, 'http://localhost:3000', 'http://localhost:3005'];
      if (!allowedOrigins.includes(event.origin)) {
        return;
      }
      
      console.log('PaciMap received message:', event.data);
      
      if (event.data.type === 'MAP_READY') {
        sendDataToIframe();
      } else if (event.data.type === 'OPEN_DETAIL' && event.data.landId) {
        // Find the land by ID and open detail dialog
        const land = lands.find(l => l.id === event.data.landId);
        if (land && onOpenDetail) {
          console.log('Opening detail for land:', land.id);
          onOpenDetail(land);
        }
      } else if (event.data.type === 'OPEN_PROPERTY_DETAILS' && event.data.landId) {
        // Handle property details request from PACI map
        const land = lands.find(l => l.id === event.data.landId);
        if (land && onOpenDetail) {
          console.log('Opening property details for land:', land.id);
          onOpenDetail(land);
        }
      }
    };

    window.addEventListener('message', handleMessage);

    // Try sending data after a short delay in case iframe is already loaded
    const timer = setTimeout(sendDataToIframe, 1000);

    return () => {
      window.removeEventListener('message', handleMessage);
      clearTimeout(timer);
    };
  }, [lands, language, onOpenDetail]);

  return (
    <div className="relative w-full h-full">
      <iframe
        ref={iframeRef}
        src="/paci-map"
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          display: 'block'
        }}
        title="PACI Map"
      />
      
      {/* Compass and Action Buttons Overlay */}
      <div className="absolute bottom-5 right-5 z-[1000] flex flex-col-reverse items-end gap-3 pointer-events-none">
        {/* Action Buttons - Vertical Stack */}
        <div className="flex flex-col gap-3 pointer-events-auto">
          {/* Agent WhatsApp Contact Button */}
          <button
            onClick={() => window.postMessage({ type: 'OPEN_WHATSAPP_DIALOG' }, '*')}
            title={language === 'ar' ? 'تواصل مع الوكلاء' : 'Contact Agents'}
            className="w-14 h-14 rounded-full bg-gradient-to-br from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-lg hover:shadow-2xl hover:scale-105 transition-all flex items-center justify-center"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M16.75 13.96c.25.13.41.2.46.3.06.11.04.61-.21 1.18-.2.56-1.24 1.1-1.7 1.12-.46.02-.47.36-2.96-.73-2.49-1.09-3.99-3.75-4.11-3.92-.12-.17-.96-1.38-.92-2.61.05-1.22.69-1.8.95-2.04.24-.26.51-.29.68-.26h.47c.15 0 .36-.06.55.45l.69 1.87c.06.13.1.28.01.44l-.27.41-.39.42c-.12.12-.26.25-.12.5.12.26.62 1.09 1.32 1.78.91.88 1.71 1.17 1.95 1.3.24.14.39.12.54-.04l.81-.94c.19-.25.35-.19.58-.11l1.67.88M12 2a10 10 0 0 1 10 10 10 10 0 0 1-10 10c-1.97 0-3.8-.57-5.35-1.55L2 22l1.55-4.65A9.969 9.969 0 0 1 2 12 10 10 0 0 1 12 2m0 2a8 8 0 0 0-8 8c0 1.72.54 3.31 1.46 4.61L4.5 19.5l2.89-.96A7.95 7.95 0 0 0 12 20a8 8 0 0 0 8-8 8 8 0 0 0-8-8z" />
            </svg>
          </button>

          {/* Bulk Share WhatsApp Button */}
          <button
            onClick={() => window.postMessage({ type: 'SHARE_BULK_WHATSAPP' }, '*')}
            title={language === 'ar' ? 'مشاركة المحددة' : 'Share Selected'}
            className="w-14 h-14 rounded-full shadow-lg transition-all flex items-center justify-center bg-blue-500 hover:bg-blue-600 text-white hover:scale-105"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z" />
            </svg>
          </button>
        </div>

        {/* Compass */}
        <div className="relative bg-white/30 rounded-full w-24 h-24 flex items-center justify-center shadow-lg backdrop-blur-sm scale-[0.7] translate-x-[22px] translate-y-[10px] pointer-events-none">
          <div className="relative w-full h-full flex items-center justify-center">
            <span className="absolute top-2 left-1/2 -translate-x-1/2 text-xs font-bold text-gray-800">N</span>
            <span className="absolute bottom-2 left-1/2 -translate-x-1/2 text-xs font-bold text-gray-800">S</span>
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-800">E</span>
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-800">W</span>
            <div className="w-1 h-1 rounded-full bg-primary" />
            <div className="absolute top-1/2 left-1/2 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-b-[20px] border-b-primary origin-bottom -translate-x-1/2 -translate-y-[60%]" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaciMap;
